/**
 * A avaliação dos quatro itens do check de prontidão de go-live (9.2) — **função pura**.
 *
 * **Eram cinco até a 11.2**: o item "Régua de dias revisada" saiu com a emenda à 9.2 (decisão 13
 * de 23/09/2026) — a ação "Confirmar revisão", a rota e o carimbo `revisado_em` não existem mais.
 *
 * Ela existe fora do componente porque é a única regra de negócio deste cartão, e porque ela é
 * testável sem render: a matriz de I/O da história inteira cabe em `avaliar-prontidao.test.ts`,
 * sem `fetch` de mentira, sem roteador e sem DOM. O que sobra no `.tsx` é desenho.
 *
 * **O check informa, e não bloqueia.** Nada aqui recusa nada: cada item devolve um estado, o
 * número que o sustenta e a **consequência escrita** de deixá-lo pendente. A decisão de virar é
 * humana, e o que este módulo entrega é o que ela precisa saber.
 *
 * **Cada item é lido de quem é dono, e não de uma tabela central**: as listas do `cadastro`, os
 * usuários da `identidade`, os números do `comunicacao` e o destino do
 * backup da `identidade`. Quem faz as chamadas é o SPA (AD-25); o que chega aqui já são as
 * respostas, e a assinatura declara isso — nenhuma função deste arquivo toca a rede.
 *
 * **Uma fonte fora do ar degrada só o item dela.** É por isso que cada entrada é uma `Fonte`, com
 * o corpo **ou** a recusa: o item vira "não foi possível verificar" com a mensagem do serviço, e
 * os outros continuam avaliados. Sem esse recorte, o `503` de um dos cinco serviços apagaria o
 * cartão inteiro — e a instalação nova, que é justamente quem mais precisa dele, é a que tem mais
 * chance de ter um serviço ainda subindo.
 */

import { MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import type { OpcoesDeDominio, TipoDeLista } from '@/modulos/cadastro/api'
import type { ProntidaoDoWhatsapp } from '@/modulos/comunicacao/api'
import type { Perfil, ProntidaoDoBackup, Usuario } from '@/modulos/identidade/api'
import { PERFIS } from '@/modulos/identidade/api'

/**
 * Uma resposta já resolvida: o corpo **ou** a recusa.
 *
 * Os dois campos são obrigatórios porque o estado "ainda carregando" **não** entra aqui: quem
 * chama espera todas as fontes assentarem antes de avaliar, e um terceiro estado nesta função
 * faria a mesma decisão ser tomada em dois lugares. `dados` indefinido sem `erro` é, portanto,
 * um caso que não acontece — e ele é tratado como indisponível em vez de virar exceção, porque
 * um cartão de prontidão que quebra a tela é pior do que um item que se declara ilegível.
 *
 * **Os dois nunca vêm preenchidos juntos, e quem garante isso é quem traduz a consulta.** Um
 * cache com dado antigo mais uma recusa no refetch seguinte é um estado real do React Query, e é
 * o pior desfecho possível **nesta** tela: o cartão existe para dizer a verdade antes da virada,
 * e "resolvido" desenhado a partir da resposta de dez minutos atrás é uma mentira que ninguém
 * tem como notar. Por isso a regra é a mais severa: **recusa apaga o dado velho**, e o item se
 * declara ilegível. `fonteDe`, em `ProntidaoDeGoLive.tsx`, é onde isso acontece.
 */
export interface Fonte<T> {
  /** O corpo que o serviço respondeu — ausente quando houve recusa, ainda que haja cache. */
  dados: T | undefined
  /** A mensagem da recusa, **verbatim**, ou `null` quando não houve recusa. */
  erro: string | null
}

/** As quatro chaves, na ordem em que o cartão as mostra. */
export type ChaveDoItem = 'listas' | 'usuarios' | 'numeros' | 'backup'

/**
 * Os três estados de um item.
 *
 * `indisponivel` é o terceiro, e ele não é um "pendente com outro nome": pendente é um fato
 * conhecido (falta configurar), e indisponível é a ausência de fato nenhum (não deu para
 * perguntar). Achatá-los faria o cartão dizer "falta configurar" sobre uma lista que talvez
 * esteja perfeita, e alguém iria configurar de novo o que já estava feito.
 */
export type SituacaoDoItem = 'resolvido' | 'pendente' | 'indisponivel'

/** Um item do check, como o cartão o desenha. */
export interface ItemDeProntidao {
  chave: ChaveDoItem
  titulo: string
  situacao: SituacaoDoItem
  /** O que sustenta o estado: o número e os nomes que faltam. */
  detalhe: string
  /**
   * A **linha curta** do cartão compacto (protótipo v2, 28/09/2026): "5 cadastrados", "1 ainda
   * não conectou (Bruno).". O `detalhe` continua existindo para o item indisponível — é nele que a
   * mensagem do serviço chega — e para quem precisar da frase inteira.
   */
  resumo: string
  /**
   * O que acontece se este item ficar como está.
   *
   * Ela vem **sempre**, inclusive no item resolvido: o cartão só a desenha ao lado do pendente,
   * mas quem lê o resultado da função (um teste, um relatório futuro) não deveria precisar
   * remontar a frase para saber o que estava em jogo.
   */
  consequencia: string
}

/* --- As consequências, escritas uma vez ------------------------------------------- */

/**
 * A consequência mais grave das cinco, e é por isso que ela é constante própria (FR-9).
 *
 * Sem um Motivo de perda cadastrado, **nenhum** Negócio consegue ser marcado como perdido: o
 * funil acumula para sempre o que já morreu, a taxa de conversão passa a mentir, e a única saída
 * de quem está na tela é excluir o Negócio — que apaga o histórico em vez de registrar o
 * desfecho.
 */
export const CONSEQUENCIA_DOS_MOTIVOS_DE_PERDA =
  'Sem Motivo de perda cadastrado, nenhum Negócio consegue ser marcado como perdido: o funil ' +
  'acumula o que já morreu e a taxa de conversão passa a mentir.'

/** A das outras quatro listas: elas não bloqueiam ato nenhum, e ainda assim custam. */
export const CONSEQUENCIA_DAS_LISTAS =
  'As fichas nascem sem esse vocabulário, e os relatórios que agrupam por ele ficam sem o que ' +
  'agrupar. Nada é bloqueado — o que se perde é a leitura da carteira.'

export const CONSEQUENCIA_DOS_USUARIOS =
  'Perfil sem ninguém ativo é trabalho que não tem quem faça: o que aquele perfil enxerga e ' +
  'move fica parado, e ninguém recebe o que deveria cair no colo dele.'

export const CONSEQUENCIA_DOS_NUMEROS =
  'Quem não conectou o número não envia nem recebe mensagem pelo CRM: as conversas dele seguem ' +
  'no celular pessoal, fora do histórico do cliente.'

export const CONSEQUENCIA_DO_BACKUP =
  'O backup fica no próprio servidor, e uma perda de máquina leva o sistema e as cópias juntos. ' +
  'Não há como restaurar o que só existia ali.'

/* --- Os rótulos ------------------------------------------------------------------- */

/**
 * O nome de cada lista, e a **ordem** em que elas são nomeadas quando faltam.
 *
 * `Record<TipoDeLista, string>` é o guarda de exaustividade, como em `ListasDeDominio.tsx`: lista
 * nova no contrato gerado quebra o build aqui, em vez de sumir do check em silêncio.
 *
 * A ordem é a mesma do cartão das listas (2.3), e não a do enum: Motivo de perda vem primeiro
 * porque é o único que bloqueia um ato do sistema.
 */
const NOME_DA_LISTA: Record<TipoDeLista, string> = {
  motivo_de_perda: 'Motivo de perda',
  origem_do_cliente: 'Origem do cliente',
  categoria: 'Categoria',
  setor: 'Setor',
}

/**
 * As quatro listas, na ordem acima — **derivadas** do mapa, e não redigitadas ao lado dele.
 *
 * Uma lista escrita à mão aqui aceitaria um subconjunto sem reclamar, e anularia a exaustividade
 * que o `Record<TipoDeLista, string>` promete: a sexta lista do contrato ganharia rótulo (o
 * `Record` obriga) e ainda assim sumiria do check, porque ninguém a teria acrescentado a esta
 * segunda lista. Derivada, ela não tem como divergir — e a ordem continua sendo a da declaração
 * do mapa, que é a do cartão das listas (2.3).
 */
const ORDEM_DAS_LISTAS = Object.keys(NOME_DA_LISTA) as readonly TipoDeLista[]

/**
 * O nome de cada perfil na tela.
 *
 * `Record<Perfil, string>` pela mesma razão: perfil novo no enum fechado do backend (AD-18)
 * quebra o build aqui, e não some do check.
 */
const NOME_DO_PERFIL: Record<Perfil, string> = {
  admin: 'Administrador',
  gestor_comercial: 'Gestor comercial',
  vendedor_closer: 'Vendedor closer',
  sdr: 'SDR',
}

/**
 * O título de cada item.
 *
 * **Eles não repetem o nome dos cartões vizinhos**, e a diferença é deliberada: "Listas de
 * domínio" e "Régua de dias" são os títulos dos cartões que **configuram** essas coisas, logo
 * abaixo neste mesmo `/configuracao`. Um item com o mesmo nome do cartão faria a tela ter dois
 * cabeçalhos idênticos, e quem navega por marcos (ou por leitor de tela) não saberia em qual
 * entrou. O que este cartão nomeia é o **estado** — preenchidas, conectados.
 */
const TITULOS: Record<ChaveDoItem, string> = {
  listas: 'Listas de domínio',
  usuarios: 'Usuários por papel',
  numeros: 'Números de WhatsApp',
  backup: 'Destino do backup',
}

/** O resumo do item cuja fonte caiu — a mensagem do serviço fica no `detalhe`. */
export const NAO_VERIFICADO = 'Não foi possível verificar.'

/** "Bruno", e não "Bruno Sakai": o cartão compacto cabe numa linha por item. */
function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome
}

/** "a, b e c" — a vírgula separa e o "e" fecha, como se escreve em português. */
function enumerar(nomes: readonly string[]): string {
  if (nomes.length <= 1) {
    return nomes[0] ?? ''
  }
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`
}

/**
 * O item que não deu para verificar — a mensagem do serviço, verbatim.
 *
 * `MENSAGEM_DE_ERRO_DESCONHECIDO` é o fundo do poço: uma falha de rede nem chega a ter mensagem,
 * e um item em branco pareceria resolvido.
 */
function indisponivel(chave: ChaveDoItem, erro: string | null, consequencia: string) {
  return {
    chave,
    titulo: TITULOS[chave],
    situacao: 'indisponivel' as const,
    detalhe: erro ?? MENSAGEM_DE_ERRO_DESCONHECIDO,
    resumo: NAO_VERIFICADO,
    consequencia,
  }
}

/* --- Os quatro itens -------------------------------------------------------------- */

/**
 * Item 1 — as quatro listas de domínio do `cadastro` (a Previsão de fechamento, a quinta do
 * FR-67, nasce com três itens de fábrica e não entra aqui — 11.3).
 *
 * Resolvido quando **as quatro** têm ao menos um valor ativo. Pendente nomeando **só as vazias**:
 * dizer "as listas estão incompletas" mandaria conferir quatro telas para achar uma.
 *
 * **A consequência muda com o conteúdo**, e este é o único item em que isso acontece: com Motivo
 * de perda vazio, o que está em jogo é o FR-9 — nenhum Negócio consegue ser marcado como perdido
 * —, e essa frase não pode ficar diluída num texto sobre relatórios.
 */
function avaliarAsListas(fonte: Fonte<OpcoesDeDominio>): ItemDeProntidao {
  if (fonte.dados === undefined) {
    return indisponivel('listas', fonte.erro, CONSEQUENCIA_DOS_MOTIVOS_DE_PERDA)
  }
  const opcoes = fonte.dados
  const vazias = ORDEM_DAS_LISTAS.filter((tipo) => opcoes[tipo].length === 0)
  if (vazias.length === 0) {
    return {
      chave: 'listas',
      titulo: TITULOS.listas,
      situacao: 'resolvido',
      detalhe: `As ${String(ORDEM_DAS_LISTAS.length)} listas têm ao menos um valor cadastrado.`,
      resumo: `As ${String(ORDEM_DAS_LISTAS.length)} listas têm valores.`,
      consequencia: CONSEQUENCIA_DAS_LISTAS,
    }
  }
  const semMotivo = vazias.includes('motivo_de_perda')
  return {
    chave: 'listas',
    titulo: TITULOS.listas,
    situacao: 'pendente',
    detalhe: `Vazias: ${enumerar(vazias.map((tipo) => NOME_DA_LISTA[tipo]))}.`,
    resumo: `Vazias: ${enumerar(vazias.map((tipo) => NOME_DA_LISTA[tipo]))}.`,
    consequencia: semMotivo ? CONSEQUENCIA_DOS_MOTIVOS_DE_PERDA : CONSEQUENCIA_DAS_LISTAS,
  }
}

/**
 * Item 2 — um usuário ativo em cada perfil (`identidade`).
 *
 * **Ativo**, e não "cadastrado": o desligado continua na lista com o nome preservado (FR-65), e
 * contá-lo faria o check dizer que a SDR está coberta por alguém que já não entra no sistema.
 *
 * Os perfis vêm de `PERFIS`, o enum fechado do backend, e não de quem apareceu na resposta: um
 * perfil sem **nenhum** usuário não teria linha nenhuma para ser notado.
 */
function avaliarOsUsuarios(fonte: Fonte<Usuario[]>): ItemDeProntidao {
  if (fonte.dados === undefined) {
    return indisponivel('usuarios', fonte.erro, CONSEQUENCIA_DOS_USUARIOS)
  }
  const ativos = fonte.dados.filter((usuario) => usuario.ativo)
  const cobertos = new Set(ativos.map((usuario) => usuario.perfil))
  const descobertos = PERFIS.filter((perfil) => !cobertos.has(perfil))
  if (descobertos.length === 0) {
    return {
      chave: 'usuarios',
      titulo: TITULOS.usuarios,
      situacao: 'resolvido',
      detalhe:
        `Os ${String(PERFIS.length)} perfis têm usuário ativo — ` +
        `${String(ativos.length)} no total.`,
      resumo: `Os ${String(PERFIS.length)} papéis têm gente ativa.`,
      consequencia: CONSEQUENCIA_DOS_USUARIOS,
    }
  }
  return {
    chave: 'usuarios',
    titulo: TITULOS.usuarios,
    situacao: 'pendente',
    detalhe: `Sem usuário ativo: ${enumerar(descobertos.map((perfil) => NOME_DO_PERFIL[perfil]))}.`,
    resumo: `Falta gente ativa em: ${enumerar(descobertos.map((perfil) => NOME_DO_PERFIL[perfil]))}.`,
    consequencia: CONSEQUENCIA_DOS_USUARIOS,
  }
}

/**
 * Item 3 — quem já conectou o próprio número (`comunicacao`, cruzado com a `identidade`).
 *
 * **A base é sempre o conjunto de usuários ativos**, e é ela que faz a conta significar alguma
 * coisa: um id conectado que não está entre os ativos — alguém desligado que tinha chip — é
 * ignorado, e não somado, senão "8 de 7" apareceria na tela.
 *
 * Ele depende de **duas** fontes, e por isso qualquer uma delas fora do ar o torna indisponível:
 * sem a lista de ativos não há denominador, e sem os ids não há numerador.
 *
 * E há um terceiro estado além de resolvido e pendente-com-nomes: **a base vazia**, que é o de
 * toda instalação recém-instalada. Ele tem ramo e frase próprios logo abaixo, e a razão está lá.
 */
function avaliarOsNumeros(
  usuarios: Fonte<Usuario[]>,
  numeros: Fonte<ProntidaoDoWhatsapp>,
): ItemDeProntidao {
  if (numeros.dados === undefined) {
    return indisponivel('numeros', numeros.erro, CONSEQUENCIA_DOS_NUMEROS)
  }
  if (usuarios.dados === undefined) {
    return indisponivel('numeros', usuarios.erro, CONSEQUENCIA_DOS_NUMEROS)
  }
  const ativos = usuarios.dados.filter((usuario) => usuario.ativo)
  const conectados = new Set(numeros.dados.usuarios_com_numero_conectado)
  const faltam = ativos.filter((usuario) => !conectados.has(usuario.id))
  const comNumero = ativos.length - faltam.length

  /*
   * **Equipe vazia é pendência, e ela precisa de frase própria.** Sem este ramo, o detalhe sairia
   * "0 de 0 usuários ativos conectaram o número." — uma contagem que descreve o nada e que se lê
   * como um defeito da tela. O que está acontecendo é outra coisa, e ela tem nome: ninguém foi
   * cadastrado ainda. **Por que ele não é `resolvido`**: "todos os ativos conectaram" seria
   * verdade num conjunto vazio, e é justamente a forma de verdade que não serve a um check —
   * numa instalação recém-instalada os cinco itens fecham em verde e a virada estaria liberada.
   * O item de usuários, logo acima, é o que diz o que fazer a respeito, e por isso este detalhe
   * o aponta em vez de repetir a lista de perfis descobertos.
   */
  if (ativos.length === 0) {
    return {
      chave: 'numeros',
      titulo: TITULOS.numeros,
      situacao: 'pendente',
      detalhe: 'Ainda não há usuário ativo para conectar um número — veja o item acima.',
      resumo: 'Ainda não há usuário ativo.',
      consequencia: CONSEQUENCIA_DOS_NUMEROS,
    }
  }

  if (faltam.length === 0) {
    return {
      chave: 'numeros',
      titulo: TITULOS.numeros,
      situacao: 'resolvido',
      detalhe: `${String(comNumero)} de ${String(ativos.length)} usuários ativos conectaram o ` +
        'número.',
      resumo: 'Todos conectados.',
      consequencia: CONSEQUENCIA_DOS_NUMEROS,
    }
  }
  return {
    chave: 'numeros',
    titulo: TITULOS.numeros,
    situacao: 'pendente',
    /*
     * Os nomes vêm **sempre** neste ramo: os dois casos em que `faltam` estaria vazia — todo mundo
     * conectado, e ninguém ativo — já saíram acima. Uma frase que dissesse "faltam 3" sem dizer
     * quais mandaria conferir a equipe inteira para achar três pessoas.
     */
    detalhe:
      `${String(comNumero)} de ${String(ativos.length)} usuários ativos conectaram o número. ` +
      `Faltam: ${enumerar(faltam.map((usuario) => usuario.nome))}.`,
    resumo:
      `${String(faltam.length)} ainda não ${faltam.length === 1 ? 'conectou' : 'conectaram'} ` +
      `(${faltam.map((usuario) => primeiroNome(usuario.nome)).join(', ')}).`,
    consequencia: CONSEQUENCIA_DOS_NUMEROS,
  }
}

/**
 * Item 4 — o destino externo do backup diário, e o desfecho da última passada (`identidade`).
 *
 * O que atravessa é um booleano e um par ok/instante: o nome do remoto é o endereço de onde mora
 * a cópia de tudo, e ele não sai do serviço — assim como o nome do pacote e o caminho do arquivo
 * de estado. O booleano é a mesma verdade que o `deploy.sh` já exige antes de subir; o par é o
 * que o agendador escreveu ao fim da última madrugada, lido pelo serviço de um volume `:ro`.
 *
 * **Uma passada que falhou não deixa o item resolvido**, mesmo com destino configurado: destino
 * apontando para um backup que não está subindo é exatamente a confiança sem cópia que o item
 * existe para impedir. `ultimo_backup` nulo (instalação que ainda não teve madrugada, ou
 * desenvolvimento, onde o contêiner de backup não existe) mantém a leitura de sempre — só o
 * destino conta.
 */
function avaliarOBackup(fonte: Fonte<ProntidaoDoBackup>): ItemDeProntidao {
  if (fonte.dados === undefined) {
    return indisponivel('backup', fonte.erro, CONSEQUENCIA_DO_BACKUP)
  }
  if (!fonte.dados.destino_de_backup_configurado) {
    return {
      chave: 'backup',
      titulo: TITULOS.backup,
      situacao: 'pendente',
      detalhe: 'Nenhum destino externo configurado para o backup diário.',
      resumo: 'Nenhum destino externo configurado.',
      consequencia: CONSEQUENCIA_DO_BACKUP,
    }
  }
  const ultimo = fonte.dados.ultimo_backup
  if (ultimo === undefined || ultimo === null) {
    return {
      chave: 'backup',
      titulo: TITULOS.backup,
      situacao: 'resolvido',
      detalhe: 'O backup diário tem destino externo configurado.',
      resumo: 'Cópia diária indo para fora do servidor.',
      consequencia: CONSEQUENCIA_DO_BACKUP,
    }
  }
  /*
   * O instante sai em data e hora locais: "quando foi a última madrugada" é meia resposta sem a
   * hora — um backup de 03h e um de 15h contam histórias diferentes sobre o agendador.
   */
  const quando = new Date(ultimo.instante).toLocaleString('pt-BR')
  if (!ultimo.ok) {
    return {
      chave: 'backup',
      titulo: TITULOS.backup,
      situacao: 'pendente',
      detalhe: `A última passada do backup falhou (${quando}). O destino está configurado.`,
      resumo: `A última passada falhou (${quando}).`,
      consequencia: CONSEQUENCIA_DO_BACKUP,
    }
  }
  return {
    chave: 'backup',
    titulo: TITULOS.backup,
    situacao: 'resolvido',
    detalhe: `Destino configurado; último backup concluído em ${quando}.`,
    resumo: 'Cópia diária indo para fora do servidor.',
    consequencia: CONSEQUENCIA_DO_BACKUP,
  }
}

/** O que a avaliação recebe: uma `Fonte` por serviço dono. */
export interface FontesDaProntidao {
  listas: Fonte<OpcoesDeDominio>
  usuarios: Fonte<Usuario[]>
  numeros: Fonte<ProntidaoDoWhatsapp>
  backup: Fonte<ProntidaoDoBackup>
}

/**
 * Os quatro itens, **sempre os quatro e nesta ordem**.
 *
 * A lista é fixa de propósito: um item que sumisse quando a fonte dele caísse faria o cartão
 * encolher em silêncio, e quem estivesse conferindo a virada não teria como saber que faltou
 * olhar alguma coisa. Fonte fora do ar vira `indisponivel`, e continua na tela.
 *
 * A ordem é a do trabalho de configurar: vocabulário, gente, canal, infraestrutura.
 */
export function avaliarProntidao(fontes: FontesDaProntidao): ItemDeProntidao[] {
  return [
    avaliarAsListas(fontes.listas),
    avaliarOsUsuarios(fontes.usuarios),
    avaliarOsNumeros(fontes.usuarios, fontes.numeros),
    avaliarOBackup(fontes.backup),
  ]
}
